import React, { useEffect, useState, useMemo } from 'react'
import ColorSwatch from "@components/product/option-selector/swatch"
import { filteredAndSortedOptions, formatProductOptions } from "@utils/product"
import { trackEvent } from "@src/services/analytics"
import { useSiteSettingsStore } from "@src/hooks/useStore"

const CompareProduct = ({ product }) => {
  const [currentVariant, setCurrentVariant] = useState(product?.variants[0]);

  const filteredOptions = useMemo(
    () =>
      filteredAndSortedOptions(
        formatProductOptions(product?.productOptions),
        product?.variants
      ),
    [product?.productOptions, product?.variants]
  );

  const handleOptionChange = (colorValue) => {
    const selectedVariant = product.variants.find((variant) =>
      variant.selectedOptions.some(
        (option) => option.name === "Color" && option.value === colorValue
      )
    );

    if (selectedVariant) {
      setCurrentVariant({ ...selectedVariant });
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="w-[150px] md:w-[200px] h-auto bg-gray-200 flex flex-col items-center justify-center rounded-md p-2">
        <img
          src={currentVariant?.listingImage?.asset?.url}
          alt={product.title}
          className="w-full h-[150px] md:h-[200px] object-cover rounded-md"
          height={200}
          width={200}
          loading="lazy"
        />
        <div className="mt-2 flex justify-center w-full gap-x-1.5 min-h-[22px]">
          <ColorSwatch
            option={filteredOptions?.find(
              (option) => option.name === "Color" || option.name === "Flavor"
            )}
            handleOptionChange={(originalIndex, { target: { value } }) => {
              handleOptionChange(value);
            }}
            variants={product?.variants}
            currentVariant={currentVariant}
            compactWidth={true}
            className="flex justify-center w-full gap-x-1.5"
          />
        </div>
      </div>
      <p className="mt-2 font-semibold text-md">{product.title}</p>
      <p className="text-xs text-gray-500 text-darker-gray">
        {product.title.includes("Revol") ? "Premium Collapsible" : "Modular Design"}
      </p>
      <p className="text-md font-semibold">
        From ${currentVariant?.price || "N/A"}
      </p>
    </div>
  );
};

const CompareAtCrates = ({ showCompareModal, setShowCompareModal }) => {
  const { siteSettings } = useSiteSettingsStore(["siteSettings"]);
  const comparisonTable = siteSettings?.compareProducts?.comparisonTable
  const productsToCompare = siteSettings?.compareProducts?.products
  const title = siteSettings?.compareProducts?.title

  useEffect(() => {
    if (!showCompareModal) return;
    trackEvent("compare_crates_click", {});
  }, [showCompareModal]);

  useEffect(() => {
    if (showCompareModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showCompareModal]);

  if (!showCompareModal) return null;

  return (
    <div className="fixed inset-0 w-screen h-auto z-[9999] flex items-center justify-center">
      <div
        className="absolute overflow-y inset-0 bg-black opacity-30"
        onClick={() => setShowCompareModal(false)}
      ></div>
      <div className="pointer-events-auto relative bg-white w-[90%] md:w-[70%] lg:w-[60%] max-h-[88%] overflow-auto py-4 rounded-lg shadow-lg">
        <button
          className="absolute top-3 right-6 flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-md hover:bg-gray-200 transition duration-300"
          onClick={() => setShowCompareModal(false)}
        >
          ✕
        </button>
        <h2 className="text-xl font-semibold text-left pl-6 pb-2 mb-2 border-b border-b-gray">
          {title}
        </h2>
        <div className="px-6 pb-6 md:pb-3 grid grid-cols-2 gap-4">
          {productsToCompare?.map((product, index) => (
            <CompareProduct key={index} product={product} />
          ))}
        </div>

        <div className="px-6 mt-1">
          {comparisonTable?.map(({ label, descriptions }, idx) => (
            <div key={idx} className="pt-2">
              <div className="text-gray-700 font-medium">{label}</div>
              <div className="grid grid-cols-2 gap-2">
                {descriptions.map((text, index) => (
                  <div key={index} className="text-gray-700 p-3 bg-light-gray rounded-md w-full">
                    {text}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompareAtCrates;
